/* eslint-disable @typescript-eslint/naming-convention */
import { Static, Type } from '@sinclair/typebox';
import { SurveyStates } from '../../../enums/SurveyStates';
import { createPathGuidParameter } from '../../../routes/createPathParameter';
import { downloadResponse, notFoundResponse, okResponse } from '../../../routes/defaultResponses';
import { makeNullable } from '../../../utils/typeboxHelpers';
import { zxyObjectTableSchema } from '../../object/object/zxyObjectTableSchema';
import { SortDirections, sortColumnsSchema } from '../../sortColumnsSchema';
import { userTableSchema } from '../../user/userTableSchema';
import { surveyTableSchema } from './surveyTableSchema';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getLongRunningSurveysSortColumnNamesSchema = Type.Union([
    Type.Literal('objects.name'),
    Type.Literal('surveys.inspectionStandardType'),
    Type.Literal('users.lastName'),
    Type.Literal('surveys.reportDeadlineOnDate'),
    Type.Literal('surveys.statusUpdatedOn'),
    Type.Literal('surveys.surveryedOn'),
    Type.Literal('surveys.status'),
]);

export type GetLongRunningSurveysSortColumnNames = Static<typeof getLongRunningSurveysSortColumnNamesSchema>;

const getNotCompletedSurveysSortColumnNamesSchema = Type.Union([
    Type.Literal('objects.name'),
    Type.Literal('surveys.inspectionStandardType'),
    Type.Literal('users.lastName'),
    Type.Literal('surveys.reportDeadlineOnDate'),
    Type.Literal('surveys.assignmentUpdatedOn'),
    Type.Literal('surveys.surveryedOn'),
    Type.Literal('surveys.status'),
]);

export type GetNotCompletedSurveysSortColumnNames = Static<typeof getNotCompletedSurveysSortColumnNamesSchema>;

const getNotCompletedSurveysSearchParametersSchema = Type.Object({
    name: Type.Optional(Type.String()),
    status: Type.Optional(Type.Enum(SurveyStates)),
    assignedToUserId: Type.Optional(Type.String({ format: 'uuid' })),
    limitFutureSurveysInDays: Type.Optional(Type.String()),
});

export type GetNotCompletedSurveysSearchParameters = Static<typeof getNotCompletedSurveysSearchParametersSchema>;

const bulkUpdateLabelAction = Type.Enum({ Replace: 'replace', Add: 'add' });

export type BulkUpdateLabelAction = Static<typeof bulkUpdateLabelAction>;

export const surveyPaths = {
    '/api/surveys/download/excel': {
        get: {
            operationId: 'surveyControllers.downloadExcel',
            parameters: [
                { name: 'languageCode', in: 'query', schema: Type.String(), required: true },
                { name: 'description', in: 'query', schema: Type.String() },
                { name: 'inspectionStandardType', in: 'query', schema: Type.String() },
                { name: 'conditions', in: 'query', schema: Type.String() },
                { name: 'companyId', in: 'query', schema: Type.String({ format: 'uuid' }) },
                { name: 'surveyorOperatorCompanyId', in: 'query', schema: Type.String({ format: 'uuid' }) },
                { name: 'status', in: 'query', schema: Type.String() },
                { name: 'labels', in: 'query', schema: Type.Array(Type.String()) },
                { name: 'batchId', in: 'query', schema: Type.String({ format: 'uuid' }) },
                { name: 'location', in: 'query', schema: Type.String() },
                { name: 'risk', in: 'query', schema: Type.String() },
                { name: 'surveyedOn', in: 'query', schema: Type.Object({ greaterThanOrEqual: Type.String(), lessThanOrEqual: Type.String() }) },
            ],
            responses: {
                ...downloadResponse,
                ...notFoundResponse,
            },
        },
    },
    '/api/surveys/bulkUpdate': {
        post: {
            operationId: 'surveyControllers.bulkUpdate',
            parameters: [
                { name: 'description', in: 'query', schema: Type.String() },
                { name: 'inspectionStandardType', in: 'query', schema: Type.String() },
                { name: 'conditions', in: 'query', schema: Type.String() },
                { name: 'companyId', in: 'query', schema: Type.String({ format: 'uuid' }) },
                { name: 'surveyorOperatorCompanyId', in: 'query', schema: Type.String({ format: 'uuid' }) },
                { name: 'status', in: 'query', schema: Type.String() },
                { name: 'labels', in: 'query', schema: Type.Array(Type.String()) },
                { name: 'batchId', in: 'query', schema: Type.String({ format: 'uuid' }) },
                { name: 'location', in: 'query', schema: Type.String() },
                { name: 'risk', in: 'query', schema: Type.String() },
                { name: 'surveyedOn', in: 'query', schema: Type.Object({ greaterThanOrEqual: Type.String(), lessThanOrEqual: Type.String() }) },
            ],
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            status: Type.Optional(Type.String()),
                            labels: Type.Optional(Type.Array(Type.String())),
                            labelAction: Type.Optional(bulkUpdateLabelAction),
                        }),
                    },
                },
            },
            responses: {
                ...okResponse,
            },
        },
    },
    '/api/surveys/{surveyId}/copyToObject': {
        parameters: [createPathGuidParameter('surveyId')],
        post: {
            operationId: 'surveyControllers.copySurvey',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                newSurveyId: Type.String(),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            destinationObjectId: Type.String(),
                        }),
                    },
                },
            },
        },
    },
    '/api/objects/{objectId}/surveysPaged': {
        parameters: [createPathGuidParameter('objectId')],
        get: {
            operationId: 'surveyControllers.getObjectSurveys',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                total: Type.Number(),
                                items: Type.Array(
                                    Type.Object({
                                        id: Type.String(),
                                        description: Type.String(),
                                        labels: Type.Array(Type.String()),
                                        careCondition: Type.String(),
                                        condition: Type.String(),
                                        craInspectionCondition: Type.Number(),
                                        craInitialHistoryCondition: Type.Number(),
                                        inspectionStandardType: Type.String(),
                                        status: Type.String(),
                                        surveryedOn: Type.Optional(Type.String()),
                                        canDelete: Type.Optional(Type.Union([Type.Boolean(), Type.Boolean()])),
                                        canEdit: Type.Optional(Type.Union([Type.Boolean(), Type.Boolean()])),
                                    }),
                                ),
                            }),
                        },
                    },
                },
            },
            parameters: [
                { name: 'limit', in: 'query', schema: Type.String(), required: true },
                { name: 'offset', in: 'query', schema: Type.String(), required: true },
                { name: 'sortColumns', in: 'query', schema: sortColumnsSchema, required: true },
                { name: 'labels', in: 'query', schema: Type.Array(Type.String()) },
                { name: 'description', in: 'query', schema: Type.String() },
            ],
        },
    },
    '/api/surveymarkers': {
        get: {
            operationId: 'survey.getAllMarkers',
            parameters: [
                { name: 'limit', in: 'query', schema: Type.String(), required: true },
                { name: 'surveyLabels', in: 'query', schema: Type.Array(Type.String()) },
                { name: 'boundingBoxXMin', in: 'query', schema: Type.String(), required: true },
                { name: 'boundingBoxYMin', in: 'query', schema: Type.String(), required: true },
                { name: 'boundingBoxXMax', in: 'query', schema: Type.String(), required: true },
                { name: 'boundingBoxYMax', in: 'query', schema: Type.String(), required: true },
                { name: 'objectTypeId', in: 'query', schema: Type.String() },
                { name: 'locationCode', in: 'query', schema: Type.String() },
                { name: 'codeDescription', in: 'query', schema: Type.String() },
                { name: 'risk', in: 'query', schema: Type.String() },
                { name: 'batchId', in: 'query', schema: Type.String() },
                { name: 'inspectionStandardType', in: 'query', schema: Type.String() },
                { name: 'conditions', in: 'query', schema: Type.String() },
            ],
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                items: Type.Array(
                                    Type.Intersect([
                                        Type.Pick(zxyObjectTableSchema, ['id', 'name', 'location', 'latitude', 'longitude']),
                                        Type.Object({
                                            lastSurveyId: Type.String(),
                                            shapeGeoJson: Type.String(),
                                            lastSurveyCondition: makeNullable(Type.String()),
                                            lastSurveyInspectionStandardType: Type.String(),
                                        }),
                                    ]),
                                ),
                                hasItemsInBoundingBox: Type.Boolean(),
                            }),
                        },
                    },
                },
            },
        },
    },

    '/api/surveys/{surveyId}/assignment': {
        parameters: [createPathGuidParameter('surveyId')],
        post: {
            operationId: 'surveyControllers.updateAssignment',
            responses: {
                ...okResponse,
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            assignedToUserId: makeNullable(Type.String({ format: 'uuid' })),
                            status: Type.Enum(SurveyStates),
                            comment: Type.String(),
                        }),
                    },
                },
            },
        },
    },
    '/api/surveys/{surveyId}/assignment/allowedUsers': {
        parameters: [createPathGuidParameter('surveyId')],
        get: {
            operationId: 'surveyControllers.getAssignmentAllowedUsers',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Array(Type.Pick(userTableSchema, ['id', 'firstName', 'lastName'])),
                        },
                    },
                },
            },
        },
    },

    '/api/surveys/{surveyId}/assignment/allowedStates': {
        parameters: [createPathGuidParameter('surveyId')],
        get: {
            operationId: 'surveyControllers.getAssignmentAllowedStates',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Array(Type.Enum(SurveyStates)),
                        },
                    },
                },
            },
        },
    },

    '/api/surveys/longRunning': {
        get: {
            operationId: 'surveyControllers.getLongRunning',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                items: Type.Array(
                                    Type.Intersect([
                                        Type.Pick(surveyTableSchema, [
                                            'id',
                                            'description',
                                            'labels',
                                            'inspectionStandardType',
                                            'status',
                                            'reportDeadlineOnDate',
                                            'statusUpdatedOn',
                                            'surveryedOn',
                                        ]),
                                        Type.Object({
                                            object: Type.Pick(zxyObjectTableSchema, ['name']),
                                            surveyComment: Type.Object({ text: makeNullable(Type.String()) }),
                                            assignedToUser: Type.Object({
                                                firstName: makeNullable(Type.String()),
                                                lastName: makeNullable(Type.String()),
                                            }),

                                            permissions: Type.Object({ canUpdate: Type.Boolean() }),
                                        }),
                                    ]),
                                ),
                            }),
                        },
                    },
                },
            },
            parameters: [
                {
                    name: 'sortColumns',
                    in: 'query',
                    schema: Type.Array(
                        Type.Object({
                            columnName: Type.Union([
                                Type.Literal('objects.name'),
                                Type.Literal('surveys.inspectionStandardType'),
                                Type.Literal('users.lastName'),
                                Type.Literal('surveys.reportDeadlineOnDate'),
                                Type.Literal('surveys.statusUpdatedOn'),
                                Type.Literal('surveys.surveryedOn'),
                                Type.Literal('surveys.status'),
                            ]),
                            direction: Type.Enum(SortDirections),
                        }),
                    ),
                    required: true,
                },
                {
                    name: 'searchParameters',
                    in: 'query',
                    schema: Type.Object({
                        name: Type.Optional(Type.String()),
                        status: Type.Optional(Type.Enum(SurveyStates)),
                        ownerCompanyId: Type.Optional(Type.String({ format: 'uuid' })),
                    }),
                },
            ],
        },
    },

    '/api/surveys/assigned': {
        get: {
            operationId: 'surveyControllers.getAssigned',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                items: Type.Array(
                                    Type.Intersect([
                                        Type.Pick(surveyTableSchema, [
                                            'id',
                                            'description',
                                            'labels',
                                            'inspectionStandardType',
                                            'status',
                                            'reportDeadlineOnDate',
                                            'assignmentUpdatedOn',
                                            'surveryedOn',
                                        ]),
                                        Type.Object({
                                            object: Type.Pick(zxyObjectTableSchema, ['name']),
                                            surveyComment: Type.Object({ text: makeNullable(Type.String()) }),
                                        }),
                                    ]),
                                ),
                            }),
                        },
                    },
                },
            },
        },
    },

    '/api/surveys/notCompleted': {
        get: {
            operationId: 'surveyControllers.getNotCompleted',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                items: Type.Array(
                                    Type.Intersect([
                                        Type.Pick(surveyTableSchema, [
                                            'id',
                                            'description',
                                            'labels',
                                            'inspectionStandardType',
                                            'status',
                                            'reportDeadlineOnDate',
                                            'statusUpdatedOn',
                                            'assignmentUpdatedOn',
                                            'surveryedOn',
                                        ]),
                                        Type.Object({
                                            object: Type.Pick(zxyObjectTableSchema, ['name']),
                                            surveyComment: Type.Object({ text: makeNullable(Type.String()) }),
                                            assignedToUser: Type.Object({
                                                firstName: makeNullable(Type.String()),
                                                lastName: makeNullable(Type.String()),
                                            }),

                                            permissions: Type.Object({ canUpdate: Type.Boolean() }),
                                        }),
                                    ]),
                                ),
                            }),
                        },
                    },
                },
            },
            parameters: [
                {
                    name: 'sortColumns',
                    in: 'query',
                    schema: Type.Array(
                        Type.Object({
                            columnName: getNotCompletedSurveysSortColumnNamesSchema,
                            direction: Type.Enum(SortDirections),
                        }),
                    ),
                    required: true,
                },
                {
                    name: 'searchParameters',
                    in: 'query',
                    schema: getNotCompletedSurveysSearchParametersSchema,
                },
            ],
        },
    },

    '/api/surveys/notCompletedExportData': {
        get: {
            operationId: 'surveyControllers.getNotCompletedExportData',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/xml': {
                            schema: {
                                type: 'object',
                                xml: { name: 'surveys' },
                                properties: {
                                    items: {
                                        type: 'array',
                                        xml: { wrapped: false },
                                        items: {
                                            type: 'object',
                                            xml: { name: 'survey' },
                                            properties: {
                                                object_name: { type: 'string', xml: { name: 'object_name' } },
                                                survey_id: { type: 'string', xml: { name: 'survey_id' } },
                                                description: { type: 'string', xml: { name: 'description' } },
                                                statusUpdatedOn: {
                                                    type: 'string',
                                                    format: 'date-time',
                                                    xml: { name: 'statusUpdatedOn' },
                                                },
                                                reportDeadlineOnDate: {
                                                    type: ['string', 'null'],
                                                    format: 'date-time',
                                                    xml: { name: 'reportDeadlineOnDate' },
                                                },
                                                assignmentUpdatedOn: {
                                                    type: ['string', 'null'],
                                                    format: 'date-time',
                                                    xml: { name: 'assignmentUpdatedOn' },
                                                },
                                                status: { type: 'string', xml: { name: 'status' } },
                                                referenceNumber: { type: ['string', 'null'], xml: { name: 'referenceNumber' } },
                                                owner_company: { type: 'string', xml: { name: 'owner_company' } },
                                                assigned_user_first_name: { type: ['string', 'null'], xml: { name: 'assigned_user_first_name' } },
                                                assigned_user_last_name: { type: ['string', 'null'], xml: { name: 'assigned_user_last_name' } },
                                                assigned_user_email: { type: ['string', 'null'], xml: { name: 'assigned_user_email' } },
                                                surveyComment: {
                                                    type: 'object',
                                                    xml: { name: 'surveyComment' },
                                                    properties: {
                                                        text: { type: ['string', 'null'], xml: { name: 'text' } },
                                                    },
                                                },
                                                surveyLink: {
                                                    type: 'string',
                                                    format: 'uri',
                                                    xml: { name: 'surveyLink' },
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
            parameters: [
                {
                    name: 'api_key',
                    in: 'query',
                    schema: Type.String(),
                    required: true,
                },
            ],
        },
    },
    '/api/surveys/permissions': {
        get: {
            operationId: 'surveysController.getPermissions',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                canReadLongRunningSurveys: Type.Boolean(),
                                canReadNotCompletedSurveys: Type.Boolean(),
                                canManageLabels: Type.Boolean(),
                                canBulkUpdate: Type.Boolean(),
                            }),
                        },
                    },
                },
            },
        },
    },
    '/api/surveys/{surveyId}/updateAssetsInReport': {
        parameters: [createPathGuidParameter('surveyId')],
        post: {
            operationId: 'surveyControllers.updateAssetsInReport',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                updated: Type.Boolean(),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            select: Type.Boolean(),
                        }),
                    },
                },
            },
        },
    },
} as const;
